import type { ChangeEventHandler } from "react";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import { useConfig } from "@jti/app-context/useConfig";
import { toast } from "@jti/core-components/components/Toaster";
import type { FlavorsSelection, OrderFrequency, PackagingPreference } from "@jti/fe-common";
import { usePdpLink } from "@jti/magento";
import { ConsumablesFormStepSection } from "@jti/ui/components/ConsumablesFormStepSection";
import { Form } from "@jti/ui/components/Form";
import { MultipleFlavorsSelector } from "@jti/ui/components/MultipleFlavorsSelector";
import { SingleFlavorSelector } from "@jti/ui/components/SingleFlavorSelector";
import { useAddProduct } from "@jti/ui/hooks/useAddProduct/useAddProduct";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import { ProductCta } from "@product-details/components/ProductCta/ProductCta";
import { SticksFormSubmitButton } from "@product-details/components/SticksFormSubmitButton";
import type { SticksProductWithAggregations } from "@product-details/containers/SticksProductDetailsContainer/types";
import { useProductData } from "@product-details/hooks/useProductData";
import { useSticksDataContext } from "@product-details/providers";
import {
  getBundleConfig,
  getCurrentCategorySkusFromSticksMatrix,
  getMixedConsumableFlavors,
  getSingleFlavorConsumable,
  handleError,
  isVisibleByStockStatus,
  updateConsumablesMatrixQuantitySearchParam,
} from "@product-details/utils";
import { getBundleProductCartPayload } from "@product-details/utils/getBundleProductCartPayload";
import { getSingleFlavorBundleProductCartPayload } from "@product-details/utils/getSingleFlavorBundleProductCartPayload";

import { OrderFrequencyGroup } from "./components/OrderFrequencyGroup";
import { PackagingPreferenceGroup } from "./components/PackagingPreferenceGroup";
import { QuantityGroup } from "./components/QuantityGroup";
import { getInitialQuantity } from "./utils/getInitialQuantity/getInitialQuantity";

import { useSetAggregation } from "./hooks";
import type { SticksFormValues } from "./SticksForm.types";
import {
  getAggregationFromLastSingleProduct,
  handleOtherPackagingTransition,
  handleSinglePacksTransition,
} from "./utils";

import styles from "./SticksForm.module.scss";

type SticksFormProps = {
  isSubscriptionsEnabled?: boolean;
  isOutOfStockProductSelected?: boolean;
  isCartLoaded: boolean;
  isQuantityLimitReached: boolean;
  setIsSubscriptionSelected: (isSelected: boolean) => void;
};

const isValidFrequencyValue = (inputValue: string): inputValue is OrderFrequency => {
  return ["subscription", "onetime"].includes(inputValue);
};

const isValidPackagingValue = (inputValue: string): inputValue is PackagingPreference => {
  return ["single_packs", "cartons", "mix_n_match"].includes(inputValue);
};

export const SticksForm = ({
  isSubscriptionsEnabled = false,
  isOutOfStockProductSelected = false,
  isCartLoaded,
  isQuantityLimitReached,
  setIsSubscriptionSelected,
}: SticksFormProps) => {
  const { __ } = useTranslations();
  const {
    data: { sticksData, sku: currentSku, customBuyCtaLabel, productCta, isCommerceDisabled },
    setCurrentSKU,
    currentProduct,
  } = useSticksDataContext();
  const { setAggregation } = useSetAggregation();
  const [multipleFlavorsSelected, setMultipleFlavorsSelected] = useState<FlavorsSelection>({});
  const [lastSingleProductSelected, setLastSingleProductSelected] = useState<SticksProductWithAggregations>();
  const { SHOP_URL, PDP_URL, displayOutOfStockStickProducts, displayAvailableInStoresStickProducts } = useConfig();

  const [showValidation, setShowValidation] = useState(false);

  const currentSticksURL = usePdpLink(currentSku, { shopUrl: SHOP_URL, pdpUrl: PDP_URL ?? "" });

  const form = useForm<SticksFormValues>({
    defaultValues: {
      orderFrequency: currentProduct?.current.orderFrequency,
      packagingPreference: currentProduct?.current.packagingPreference,
      flavors: {},
      quantity: getInitialQuantity(currentProduct?.current.packagingPreference),
    },
  });
  const { watch, setValue } = form;
  const { packagingPreference, quantity, orderFrequency } = watch();

  useEffect(() => {
    updateConsumablesMatrixQuantitySearchParam(currentSticksURL, quantity);
  }, [currentSticksURL, quantity]);

  useEffect(() => {
    if (currentProduct.current.packagingPreference !== "mix_n_match") {
      setLastSingleProductSelected(currentProduct);
    }
  }, [currentProduct.current.sku, currentProduct.current.packagingPreference]);

  const flavorsAvailableForCurrentVariant = sticksData[packagingPreference][orderFrequency];

  const onError = () => {
    setShowValidation(true);
  };

  const { isRunning: isAddProductInProgress, addProductToCart } = useAddProduct(currentSku, undefined, false, onError);

  const categoriesSKUList = useMemo(
    () => getCurrentCategorySkusFromSticksMatrix(packagingPreference, orderFrequency, sticksData),
    [packagingPreference, orderFrequency, sticksData]
  );

  const {
    elementsStockStatus,
    isProductDataLoading,
    isProductDataLoaded,
    siblingItemsStockStatus,
    stockStatus,
    finalPrices,
  } = useProductData({
    sku: currentSku,
    currentVariantSku: currentSku,
    categoriesSKUList,
  });

  const [selectedBundleConfig] = getBundleConfig(
    currentProduct.current.product?.productBundleConfiguration?.items
  ).sticks;

  const multipleFlavorsSticks = useMemo(() => {
    if (packagingPreference !== "mix_n_match" || !selectedBundleConfig?.allOptions) return [];

    const multipleFlavorsSticks = selectedBundleConfig.allOptions.map(getMixedConsumableFlavors);

    if (displayOutOfStockStickProducts && displayAvailableInStoresStickProducts) {
      return multipleFlavorsSticks;
    }

    return multipleFlavorsSticks.filter((flavor) => {
      if (!Object.hasOwn(elementsStockStatus, flavor.id)) return false;

      return isVisibleByStockStatus(elementsStockStatus[flavor.id], {
        displayOutOfStock: displayOutOfStockStickProducts,
        displayAvailableInStores: displayAvailableInStoresStickProducts,
      });
    });
  }, [
    packagingPreference,
    selectedBundleConfig?.allOptions,
    elementsStockStatus,
    displayOutOfStockStickProducts,
    displayAvailableInStoresStickProducts,
  ]);

  const singleFlavorSticks = useMemo(() => {
    if (packagingPreference === "mix_n_match") return [];

    const singleFlavorSticks = flavorsAvailableForCurrentVariant.map(getSingleFlavorConsumable);

    if (displayOutOfStockStickProducts && displayAvailableInStoresStickProducts) {
      return singleFlavorSticks;
    }

    return singleFlavorSticks.filter((flavor) => {
      if (!Object.hasOwn(siblingItemsStockStatus, flavor.sku)) return false;

      return isVisibleByStockStatus(siblingItemsStockStatus[flavor.sku], {
        displayOutOfStock: displayOutOfStockStickProducts,
        displayAvailableInStores: displayAvailableInStoresStickProducts,
      });
    });
  }, [
    packagingPreference,
    flavorsAvailableForCurrentVariant,
    siblingItemsStockStatus,
    displayOutOfStockStickProducts,
    displayAvailableInStoresStickProducts,
  ]);

  const setSelectedFlavors = (flavorsSelection: FlavorsSelection) => {
    setMultipleFlavorsSelected(flavorsSelection);
    form.setValue("flavors", flavorsSelection);
  };

  const handleSubmit = (formValues: SticksFormValues) => {
    setShowValidation(false);
    const quantity = Number(formValues.quantity);

    if (formValues.packagingPreference === "mix_n_match") {
      const payload = getBundleProductCartPayload(selectedBundleConfig?.optionId, formValues.flavors);

      return addProductToCart(quantity, payload);
    }

    if (formValues.packagingPreference === "single_packs") {
      return addProductToCart(quantity);
    }

    if (selectedBundleConfig === undefined) {
      handleError("Product misconfiguration. No options available for carton bundle product.");

      return;
    }

    const payload = getSingleFlavorBundleProductCartPayload(selectedBundleConfig);

    if (!payload) {
      handleError("Something's wrong with selected bundle config");

      return;
    }

    addProductToCart(quantity, payload);
  };

  const onSelectedFlavourChange = (sku: string) => {
    if (!sku || sku === currentSku) {
      return;
    }

    setCurrentSKU(sku);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleTransitionFromSinglePacksToSubscription = () => {
    const onSuccess = () => {
      setValue("packagingPreference", "cartons");
      setValue("quantity", "1");
    };

    const aggregationSetSuccessfully = setAggregation({
      orderFrequency: "subscription",
      packagingPreference: "cartons",
      onSuccess,
    });

    if (aggregationSetSuccessfully) return;

    const cartonFallbackSetSuccessfully = setAggregation({
      orderFrequency: "subscription",
      packagingPreference: "cartons",
      shouldUseFallback: true,
      onSuccess,
    });

    if (cartonFallbackSetSuccessfully) return;

    const mix_n_matchFallbackSetSuccessfully = setAggregation({
      orderFrequency: "subscription",
      packagingPreference: "mix_n_match",
      shouldUseFallback: true,
      onSuccess,
    });

    if (mix_n_matchFallbackSetSuccessfully) return;

    toast.error(__("productdetails.sticks.form.error.product.unavailable"));
  };

  const handleChangeToSubscription = () => {
    setIsSubscriptionSelected(true);
    if (packagingPreference === "single_packs") {
      return handleTransitionFromSinglePacksToSubscription();
    }

    const aggregationSetSuccessfully = setAggregation({
      orderFrequency: "subscription",
      packagingPreference,
    });
    if (aggregationSetSuccessfully) return;

    const fallbackSetSuccessfully = setAggregation({
      orderFrequency: "subscription",
      packagingPreference,
      shouldUseFallback: true,
    });
    if (fallbackSetSuccessfully) return;

    toast.error(__("productdetails.sticks.form.error.product.unavailable"));
  };

  const onFrequencyChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const { value: newFrequencyValue } = e.target;
    // this should never happen, but required if we don't want to cast and keep typescript happy
    /* istanbul ignore next */
    if (!isValidFrequencyValue(newFrequencyValue)) return;

    setSelectedFlavors({});

    if (newFrequencyValue === "subscription") {
      return handleChangeToSubscription();
    }

    setAggregation({
      orderFrequency: "onetime",
      packagingPreference,
    });
    setIsSubscriptionSelected(false);
  };

  const handleTransitionFromSubscriptionToSinglePacks = (lastSingleProductSku?: string) => {
    const onSuccess = () => {
      setValue("orderFrequency", "onetime");
      setValue("quantity", "1");
    };
    setAggregation({
      orderFrequency: "onetime",
      packagingPreference: "single_packs",
      onSuccess,
      predefinedSku: lastSingleProductSku,
    });
    setIsSubscriptionSelected(false);
  };

  const onPackagingOptionChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const { value: newPackagingValue } = e.target;

    /* istanbul ignore next */
    if (!isValidPackagingValue(newPackagingValue)) return;

    const aggregation = getAggregationFromLastSingleProduct(
      packagingPreference,
      newPackagingValue,
      lastSingleProductSelected
    );

    if (orderFrequency === "subscription" && newPackagingValue === "single_packs") {
      return handleSinglePacksTransition(
        aggregation,
        setSelectedFlavors,
        handleTransitionFromSubscriptionToSinglePacks
      );
    }

    handleOtherPackagingTransition(aggregation, orderFrequency, newPackagingValue, setAggregation);
  };

  let step = 0;

  return (
    <Form<SticksFormValues> form={form} onSubmit={handleSubmit} dataTestId="sticks-pdp-form">
      {isSubscriptionsEnabled && (
        <OrderFrequencyGroup onChange={onFrequencyChange} step={++step} currentProduct={currentProduct} />
      )}
      <PackagingPreferenceGroup onChange={onPackagingOptionChange} currentProduct={currentProduct} step={++step} />
      <ConsumablesFormStepSection
        title={
          <>
            <strong className={styles.stepCountLabel}>{__(`sticksForm.step.order_text.${++step}`)}</strong>{" "}
            {__("sticksForm.step.flavors_selection.title")}
          </>
        }
        className={styles.stepSection}
        data-testid="flavor-selector-step"
      >
        <div style={{ width: "100%" }}>
          {packagingPreference === "mix_n_match" ? (
            <MultipleFlavorsSelector
              key={currentSku}
              limit={selectedBundleConfig?.qty}
              items={multipleFlavorsSticks}
              stockStatusElements={elementsStockStatus}
              onChange={setSelectedFlavors}
              isProductDataLoading={isProductDataLoading}
              shouldValidate={showValidation}
              defaultFlavorsSelection={multipleFlavorsSelected}
            />
          ) : (
            <SingleFlavorSelector
              key={currentSku}
              flavors={singleFlavorSticks}
              initialSelection={getSingleFlavorConsumable(currentProduct.current)}
              stockStatusElements={siblingItemsStockStatus}
              isProductDataLoading={isProductDataLoading}
              onChange={onSelectedFlavourChange}
            />
          )}
        </div>
      </ConsumablesFormStepSection>
      <QuantityGroup step={++step} />
      {stockStatus === "AVAILABLE_IN_STORES" ? (
        <ProductCta
          productCta={productCta}
          isCommerceDisabled={isCommerceDisabled}
          isProductDataLoading={isProductDataLoading}
          stockStatus={stockStatus}
          unavailableStatuses={["AVAILABLE_IN_STORES"]}
        />
      ) : (
        <SticksFormSubmitButton
          productName={currentProduct.current.product.name}
          finalPrices={finalPrices}
          quantity={+quantity}
          isProductDataLoading={isProductDataLoading}
          isProductDataLoaded={isProductDataLoaded}
          isAddProductInProgress={isAddProductInProgress}
          isOutOfStockProductSelected={isOutOfStockProductSelected}
          isCartLoaded={isCartLoaded}
          showPricePerMonthLabel={orderFrequency === "subscription"}
          customBuyCtaLabel={customBuyCtaLabel}
          isQuantityLimitReached={isQuantityLimitReached}
        />
      )}
    </Form>
  );
};
